import apiClient from './client';
import { ScheduleTask } from '../types';
import {
  validateSport,
  validateAIProvider,
  sanitizeParam
} from './validation';

// ===== Schedule API =====

export const getSchedules = async (adminId?: number | null): Promise<ScheduleTask[]> => {
  const params = new URLSearchParams();
  if (adminId !== null && adminId !== undefined) {
    params.append('admin_id', adminId.toString());
  }
  const url = params.toString() ? `/api/schedule?${params}` : '/api/schedule';
  const response = await apiClient.get(url);
  return response.data;
};

export const createSchedule = async (
  task: Omit<ScheduleTask, 'task_id' | 'created_at' | 'next_run'>,
  targetAdminId?: number | null  // administrator가 다른 관리자용으로 생성할 때
): Promise<{ status: string; task_id: string; admin_id: number; task: ScheduleTask }> => {
  // Validate sport and ai_provider in task
  const validatedTask: any = {
    ...task,
    sport: validateSport(task.sport),
  };

  // ai_provider가 있을 때만 검증 (빈 문자열이면 크롤링만)
  if (task.ai_provider && task.ai_provider.trim() !== '') {
    validatedTask.ai_provider = validateAIProvider(task.ai_provider);
  } else {
    validatedTask.ai_provider = null;  // 크롤링만 수행
  }

  // target_admin_id 추가
  if (targetAdminId !== null && targetAdminId !== undefined) {
    validatedTask.target_admin_id = targetAdminId;
  }

  const response = await apiClient.post('/api/schedule', validatedTask);
  return response.data;
};

export const updateSchedule = async (
  taskId: string,
  task: Partial<ScheduleTask>
): Promise<{ status: string; task_id: string; updated: ScheduleTask }> => {
  // Validate if sport or ai_provider are being updated
  const validatedTask = { ...task };
  if (task.sport) {
    validatedTask.sport = validateSport(task.sport);
  }
  if (task.ai_provider) {
    validatedTask.ai_provider = validateAIProvider(task.ai_provider);
  }

  const response = await apiClient.patch(`/api/schedule/${sanitizeParam(taskId)}`, validatedTask);
  return response.data;
};

export const deleteSchedule = async (
  taskId: string
): Promise<{ status: string; message: string }> => {
  const response = await apiClient.delete(`/api/schedule/${sanitizeParam(taskId)}`);
  return response.data;
};

// ===== Scheduler History API =====

// 스케줄러 실행 이력 (담당자 정보 및 실행 통계 포함)
export interface SchedulerHistoryItem {
  history_id: number;
  task_id: number | null;
  sport_name: string;
  game_id: string | null;
  match_date: string | null;
  home_team: string | null;
  away_team: string | null;
  execution_start: string;
  execution_end: string | null;
  status: string;
  pitcher_announced: boolean;
  db_saved: boolean;
  prediction_generated: boolean;
  // 실행 통계
  total_matches: number;
  matches_saved: number;
  predictions_created: number;
  notion_saved: number;
  failed_count: number;
  skipped_count: number;
  duration: number;
  // 재시도
  retry_count: number;
  next_retry: string | null;
  retry_reason: string | null;
  error_message: string | null;
  created_at: string | null;
  updated_at: string | null;
  // 담당자 정보 (연결된 스케줄 작업에서 파생)
  admin_id: number | null;
  admin_name: string | null;
  notion_admin_id: number | null;
  notion_admin_name: string | null;
  notion_admin_inherited: boolean;
  // 스케줄 실행 옵션
  ai_provider: string | null;
  crawl_target: string | null;
  schedule_time: string | null;
  prediction_only: boolean;
  auto_publish_psynet: boolean;
  task_enabled: boolean | null;
}

// 상세 조회 응답 (경기 상세 데이터 포함).
// JSON 파싱에 실패한 경우 백엔드가 원본 문자열을 그대로 반환함
export interface SchedulerHistoryDetail extends SchedulerHistoryItem {
  match_data: Record<string, any> | string | null;
}

export const getSchedulerHistory = async (
  sport?: string,
  page: number = 1,
  pageSize: number = 20,
  adminId?: number | null
): Promise<{
  status: string;
  data: SchedulerHistoryItem[];
  pagination: {
    current_page: number;
    total_pages: number;
    page_size: number;
    total_items: number;
    has_next: boolean;
    has_prev: boolean;
  };
}> => {
  const params: any = {
    page: Math.max(1, page),
    page_size: Math.min(Math.max(1, pageSize), 100)
  };
  if (sport) {
    params.sport = sport;
  }
  if (adminId !== null && adminId !== undefined) {
    params.admin_id = adminId;
  }
  const response = await apiClient.get('/api/schedule/history', { params });
  return response.data;
};

export const getSchedulerHistoryDetail = async (historyId: number): Promise<{
  status: string;
  data: SchedulerHistoryDetail;
}> => {
  const response = await apiClient.get(`/api/schedule/history/${historyId}`);
  return response.data;
};

export const getSchedulerStats = async (sport?: string, days: number = 7): Promise<{
  status: string;
  sport: string;
  days: number;
  stats: {
    total: number;
    completed: number;
    failed: number;
    retry: number;
    predictions: number;
    avg_retries: number;
  };
}> => {
  const params: any = { days: Math.min(days, 365) };
  if (sport) {
    params.sport = sport;
  }
  const response = await apiClient.get('/api/schedule/history/stats', { params });
  return response.data;
};
